import { useState } from 'react';
import { trackError } from '#api/errors.ts';
import type { AuditLog, EventType, TargetType } from '#dn-types/audit-log.ts';
import { UnreachableCodeError } from '#util/assert-unreachable.ts';

const EVENT_TYPE_MAP: Record<EventType, string> = {
  CREATED: 'created',
  UPDATED: 'edited',
  DELETED: 'deleted',
  DELETED_TOTP: 'totp deleted',
  UPDATED_TOTP: 'totp updated',
  CREATED_TOTP: 'totp created',
  SUCCEEDED_AUTH: 'authenticated successfully',
  FAILED_AUTH: 'failed to authenticate',
  ENROLLED: 'enrolled',
  RENEWED: 'renewed',
  RENEWED_NETWORK_ACCESS: 'renewed network access',
  CREATED_ENROLL_CODE: 'enrollment code created',
  SET_NETWORK_CA: 'signing CA set',
  BLOCKED_HOST: 'blocked',
  UNBLOCKED_HOST: 'unblocked',
  SET_OVERRIDES: 'config changed',
  UPDATED_SUBSCRIPTION: 'plan changed',
  CHANGED_DOMAIN_VERIFICATION: 'verification changed',
  CREATED_WEBAUTHN: 'passkey created',
  DELETED_WEBAUTHN: 'passkey deleted',
  UPDATED_WEBAUTHN: 'passkey updated',
  ENABLED_MANAGED_LIGHTHOUSE: 'managed lighthouse enabled',
  SIGNED_MANAGED_LIGHTHOUSE: 'managed lighthouse signed',
};

const TARGET_TYPE_MAP_CAPITALIZED: Record<TargetType, string> = {
  apiKey: 'API key',
  host: 'Host',
  network: 'Network',
  role: 'Role',
  route: 'Route',
  user: 'User',
  ca: 'Certificate Authority',
  oidcProvider: 'Identity Provider',
  organization: 'Organization',
  tag: 'Tag',
  endpointOIDC: 'Endpoint Identity Provider',
  domain: 'Domain',
  googleIdP: 'Google Workspace directory',
  joinKey: 'Join key',
};

const TARGET_TYPE_MAP_CAPITALIZED_PLURAL: Record<TargetType, string> = {
  apiKey: 'API keys',
  host: 'Hosts',
  network: 'Networks',
  role: 'Roles',
  route: 'Routes',
  user: 'Users',
  ca: 'Certificate Authorities',
  oidcProvider: 'Identity Providers',
  organization: 'Organization',
  tag: 'Tags',
  endpointOIDC: 'Endpoint Identity Providers',
  domain: 'Domains',
  googleIdP: 'Google Workspace directories',
  joinKey: 'Join keys',
};

export function isTargetType(item: unknown): item is TargetType {
  for (const n in TARGET_TYPE_MAP_CAPITALIZED) {
    if (item === n) {
      return true;
    }
  }
  return false;
}

export function getCapitalizedPluralTargetNoun(targetType: TargetType) {
  return TARGET_TYPE_MAP_CAPITALIZED_PLURAL[targetType];
}

/**
 * Determine what field to pull from the log's actor object, depending on its type.
 *
 * If it can't be determined, will throw an error so that we can track in Sentry.
 */
function getActorNoun(log: AuditLog) {
  switch (log.actor.type) {
    case 'user':
    case 'oidcUser':
    case 'endpointOIDCUser':
      return log.actor.email;
    case 'apiKey':
      if (log.actor.name) {
        return `API key: ${log.actor.name}`;
      }
      // If the key has been deleted, show its id

      return `API key: ${log.actor.id}`;
    case 'joinKey':
      // Enrollment events name the key that minted the host; the name is kept on the entry if the key is gone
      return `Join key: ${log.actor.name ?? log.actor.id}`;
    case 'host':
      // Enrollment/renewal events are weird, the target is also the actor, so strip it out
      if (log.event.type === 'ENROLLED' || log.event.type === 'RENEWED') {
        return null;
      }
      if (log.actor.name) {
        return log.actor.name;
      }
      // If the host has been deleted, show its ID

      return log.actor.id;
    case 'system':
      return 'system';
    case 'support':
      return 'DN Support';
    default:
      throw new UnreachableCodeError(
        log.actor,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/restrict-template-expressions
        `Could not find actor type for audit log actor: ${log.actor || 'unknown'}`
      );
  }
}

/**
 * Determine what "actor noun" should be based on an audit log.
 *
 * This is a react hook so that we send unknown actor types to Sentry,
 * and only track them once (per hook usage, at least).
 */
export function useActorNoun(log: AuditLog) {
  // Let's just track each one once, to avoid potential noise in the logs
  const [trackedLogIDs, setTrackedLogIDs] = useState<string[]>([]);

  let actorNoun: string | null;
  try {
    actorNoun = getActorNoun(log);
  } catch (e: unknown) {
    if (!trackedLogIDs.includes(log.id)) {
      trackError(e);
      setTrackedLogIDs(trackedLogIDs.concat(log.id));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const actor = log.actor as any;

    // pick the best `name` for the actor
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (true) {
      case actor.email:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        actorNoun = actor.email;
        break;
      case actor.name:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        actorNoun = actor.name;
        break;
      case actor.id:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        actorNoun = actor.id;
        break;
      default:
        actorNoun = 'unknown';
    }
  }
  return actorNoun;
}

/**
 * Given a target type, return an english noun with the first letter capitalized.
 *
 * This is a react hook so that we send unknown target types to Sentry,
 * and only track them once (per hook usage, at least).
 */
export function useCapitalizedTargetTypeNoun(targetType?: TargetType) {
  // Let's just track each one once, to avoid potential noise in the logs
  const [trackedLogIDs, setTrackedLogIDs] = useState<string[]>([]);

  // If we don't have a targetType, just return
  if (!targetType) return;

  let typeNoun = TARGET_TYPE_MAP_CAPITALIZED[targetType];

  if (!typeNoun) {
    if (!trackedLogIDs.includes(targetType)) {
      trackError(new Error(`Could not find noun for audit log targetType: ${targetType}`));
      setTrackedLogIDs(trackedLogIDs.concat(targetType));
    }
    typeNoun = 'Something';
  }

  return typeNoun;
}

/**
 * Given an event type, return an english verb or verb phrase that describes what happened.
 *
 * This is a react hook so that we send unknown event types to Sentry,
 * and only track them once (per hook usage, at least).
 */
export function useEventTypeVerb(eventType: EventType) {
  // Let's just track each one once, to avoid potential noise in the logs
  const [trackedLogIDs, setTrackedLogIDs] = useState<string[]>([]);
  let verb = EVENT_TYPE_MAP[eventType];

  if (!verb) {
    if (!trackedLogIDs.includes(eventType)) {
      trackError(new Error(`Could not find verb for audit log eventType: ${eventType}`));
      setTrackedLogIDs(trackedLogIDs.concat(eventType));
    }
    verb = 'operated on';
  }

  return verb;
}
